import { useCallback, useEffect, useMemo, useState } from 'react'
import { PAGE_SIZE, type PageResult } from '../../api'

/** 服务端分页列表：page / pageSize 变化自动请求；reload 可传 loader 额外参数（如标题搜索词） */
export function usePagedQuery<T>(
  loader: (page: number, pageSize: number, ...extra: unknown[]) => Promise<PageResult<T>>,
) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const [total, setTotal] = useState(0)
  const [items, setItems] = useState<T[]>([])
  const [loading, setLoading] = useState(false)

  const reload = useCallback(
    (p: number, ps: number = pageSize, ...extra: unknown[]) => {
      setLoading(true)
      return loader(p, ps, ...extra)
        .then((r) => {
          setItems(r.items)
          setTotal(r.total)
          setPage(r.page)
          setPageSize(r.page_size ?? ps)
        })
        .finally(() => setLoading(false))
    },
    [loader, pageSize],
  )

  useEffect(() => {
    void reload(page, pageSize)
  }, [page, pageSize, reload])

  const handlePaginationChange = useCallback(
    (p: number, ps?: number) => {
      const nextSize = ps ?? pageSize
      if (nextSize !== pageSize) {
        setPageSize(nextSize)
        setPage(1)
      } else {
        setPage(p)
      }
    },
    [pageSize],
  )

  const paginationOptions = useMemo(
    () => ({ pageSize, showSizeChanger: true as const }),
    [pageSize],
  )

  return {
    page,
    setPage,
    pageSize,
    setPageSize,
    total,
    items,
    loading,
    reload,
    handlePaginationChange,
    paginationOptions,
  }
}
